import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Card, CardBody } from "../../components/ui";
import {
  FaArrowRight,
  FaQuestionCircle,
  FaCodeBranch,
  FaRobot,
  FaBullhorn,
  FaUsers,
  FaFileExcel,
  FaCheckCircle,
  FaUserFriends,
  FaImage,
  FaCommentDots,
  FaRocket,
} from "react-icons/fa";

const guides = [
  {
    slug: "getting-started",
    icon: FaRocket,
    title: "Getting Started",
    description:
      "Learn how Walytic works and how it automates WhatsApp communication for your business. Quick setup guide, feature overview, and real-world use cases by industry.",
    color: "from-brand to-blue-600",
    badge: "Start Here",
  },
  {
    slug: "flow-builder",
    icon: FaCodeBranch,
    title: "Flow Builder",
    description:
      "Build automated WhatsApp workflows with triggers, delays, conditions, and loops. Set up cold outreach campaigns that send messages on a schedule.",
    color: "from-green-500 to-emerald-600",
    badge: "Most Popular",

  },
  {
    slug: "ai-features",
    icon: FaRobot,
    title: "AI Chatbot & Voice",
    description:
      "Connect OpenAI to power AI replies. Train your bot with knowledge bases. Convert text to voice notes with ElevenLabs.",
    color: "from-violet-500 to-purple-600",
    badge: null,
  },
  {
    slug: "campaigns",
    icon: FaBullhorn,
    title: "Campaigns",
    description:
      "Create outreach campaigns with multi-step WhatsApp sequences across multiple sessions. Target leads by tag or stage.",
    color: "from-amber-500 to-orange-600",
    badge: null,
  },
  {
    slug: "crm",
    icon: FaUsers,
    title: "CRM & Lead Management",
    description:
      "Track leads through a pipeline. Import contacts, tag them, add custom fields, and move them through stages.",
    color: "from-blue-500 to-indigo-600",
    badge: null,
  },
  {
    slug: "send-via-excel",
    icon: FaFileExcel,
    title: "Send via Excel",
    description:
      "Upload a spreadsheet with phone numbers and messages. Walytic sends them with smart delays to avoid bans.",
    color: "from-teal-500 to-cyan-600",
    badge: null,
  },
  {
    slug: "number-verification",
    icon: FaCheckCircle,
    title: "Number Verification",
    description:
      "Check if phone numbers are active on WhatsApp before messaging. Verify one by one or upload a bulk list.",
    color: "from-emerald-500 to-green-600",
    badge: null,
  },
  {
    slug: "group-export",
    icon: FaUserFriends,
    title: "Group Member Export",
    description:
      "Export phone numbers from any WhatsApp group you belong to. Download as Excel or copy to clipboard.",
    color: "from-pink-500 to-rose-600",
    badge: null,
  },
  {
    slug: "image-to-url",
    icon: FaImage,
    title: "Image & File to URL",
    description:
      "Upload images, PDFs, or documents and get a shareable URL. Use these links in your flow messages or campaigns.",
    color: "from-sky-500 to-blue-600",
    badge: null,
  },
  {
    slug: "whatsapp-widget",
    icon: FaCommentDots,
    title: "WhatsApp Widget",
    description:
      "Add a floating WhatsApp chat button to your website. Customize colors, welcome message, and quick replies.",
    color: "from-green-500 to-green-600",
    badge: null,
  },
];

export default function GuideIndex() {
  useEffect(() => {
    document.title = "User Guide | Walytic";
  }, []);

  return (
    <div className="page-content">
      <PageHeader title="User Guide" subtitle="Step-by-step guides for every feature" />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {guides.map((guide) => {
          const Icon = guide.icon;
          return (
            <Link
              key={guide.slug}
              to={`/guide/${guide.slug}`}
              className="group bg-surface-card rounded-xl border border-surface-border p-6 hover:shadow-lg hover:border-brand/30 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className={`h-12 w-12 rounded-lg bg-gradient-to-br ${guide.color} flex items-center justify-center`}
                >
                  <Icon className="h-6 w-6 text-white" />
                </div>
                {guide.badge && (
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">
                    {guide.badge}
                  </span>
                )}
              </div>
              <h3 className="font-semibold text-lg mb-2 text-ink-primary group-hover:text-brand transition-colors">
                {guide.title}
              </h3>
              <p className="text-ink-secondary text-sm mb-4 leading-relaxed">
                {guide.description}
              </p>
              <span className="inline-flex items-center text-sm font-medium text-brand group-hover:gap-2 transition-all">
                Read guide{" "}
                <FaArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          );
        })}
      </div>

      {/* Still need help */}
      <div className="mt-12">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 text-center border border-blue-100">
          <FaQuestionCircle className="h-10 w-10 text-brand mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-ink-primary mb-3">
            Can&apos;t find what you need?
          </h3>
          <p className="text-ink-secondary mb-6">
            Reach out on WhatsApp and we will walk you through it.
          </p>
          <a
            href="https://wa.me/918926314377?text=Hi%2C%20I%20need%20help%20with%20Walytic"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 bg-brand hover:opacity-90 text-white font-semibold rounded-xl shadow-lg transition-all"
          >
            Chat with Support{" "}
            <FaArrowRight className="ml-2 h-4 w-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
